import { StreamingIndicator, ToolError } from "@corpuscomplete/web/modules/common/agent-chat";
import { AgentTools, AskQuestions } from "@corpuscomplete/core-ui/ai/agent/tools";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Checkbox } from "@corpuscomplete/web-ui/components/checkbox";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { RadioGroup, RadioGroupItem } from "@corpuscomplete/web-ui/components/radio-group";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { ChevronDownIcon, ChevronUpIcon, CircleHelpIcon } from "lucide-react";
import { useRef } from "react";
import { PendingHumanToolCard } from "./pending-human-tool-card";

/**
 * Pending UI for ask-questions tool execution.
 * Contract: stores answer draft state per tool call id and only submits
 * via AgentTools when the queued step is interactive.
 */
export function ToolAskQuestionsPending({
  messageId,
  part,
  isActive,
  isPersisted,
  isQueueInteractive,
  queuePosition,
  queueTotal,
  upNextLabel,
}: {
  messageId: string;
  part: AskQuestions.Part;
  isActive: boolean;
  isPersisted: boolean;
  isQueueInteractive?: boolean;
  queuePosition: number;
  queueTotal: number;
  upNextLabel?: string;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const questionsData =
    part.state === "input-streaming" || part.state === "output-error"
      ? []
      : readQuestions(part.input);

  const {
    currentQuestion,
    currentIndex,
    totalQuestions,
    isCurrentAnswered,
    isLastQuestion,
    goToPrevious: hookGoToPrevious,
    goToNext: hookGoToNext,
    singleSelections,
    multiSelections,
    customInputs,
    setSingleSelection,
    toggleMultiSelection,
    setCustomInput,
    getAnswers,
  } = AgentTools.useQuestions({
    questions: questionsData,
    toolCallId: part.toolCallId,
  });

  const { submit, canSubmit = true } = AgentTools.useToolSubmit<{
    answers: Record<string, string | string[]>;
  }>({
    toolName: AskQuestions.toolName,
    toolCallId: part.toolCallId,
    messageId,
  });

  const { cancel, canCancel = true } = AgentTools.useToolCancel({
    toolCallId: part.toolCallId,
    messageId,
  });

  if (part.state === "input-streaming") {
    return <StreamingIndicator />;
  }

  if (part.state === "output-error") {
    return <ToolError errorText={part.errorText} />;
  }

  const isInteractive = (isQueueInteractive ?? (isActive && isPersisted)) && canSubmit;
  const isCancelInteractive = (isQueueInteractive ?? (isActive && isPersisted)) && canCancel;

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  const goToPrevious = () => {
    hookGoToPrevious();
    scrollToTop();
  };

  const goToNext = () => {
    hookGoToNext();
    scrollToTop();
  };

  const handleSubmit = () => {
    submit({ answers: getAnswers() });
  };

  const handleContinue = () => {
    if (isLastQuestion) {
      handleSubmit();
    } else {
      goToNext();
    }
  };

  const showCustomInput =
    currentQuestion?.allowCustom &&
    (currentQuestion.allowMultiple
      ? (multiSelections[currentQuestion.id] ?? []).includes(AgentTools.CUSTOM_OPTION_ID)
      : singleSelections[currentQuestion.id] === AgentTools.CUSTOM_OPTION_ID);

  if (!currentQuestion) return null;

  return (
    <PendingHumanToolCard
      icon={<CircleHelpIcon className="h-4 w-4 text-primary" />}
      title="Questions"
      progressLabel={AgentTools.formatPendingQueueProgress(queuePosition, queueTotal)}
      upNextLabel={upNextLabel}
      isActive={isActive}
      isPersisted={isPersisted}
      canCancel={part.state === "input-available" && isCancelInteractive}
      onCancel={cancel}
      contentHeight={280}
      contentRef={scrollContainerRef}
      headerRight={
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={goToPrevious}
            disabled={!isInteractive || currentIndex === 0}
          >
            <ChevronUpIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={goToNext}
            disabled={!isInteractive || currentIndex === totalQuestions - 1}
          >
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
          <Text variant="xs" className="w-12 text-right text-muted-foreground">
            {currentIndex + 1} of {totalQuestions}
          </Text>
        </>
      }
      footerActions={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNext}
            disabled={!isInteractive || isLastQuestion}
          >
            Skip
          </Button>
          <Button
            size="sm"
            onClick={handleContinue}
            disabled={!isInteractive || (!isLastQuestion && !isCurrentAnswered)}
          >
            {isLastQuestion ? "Submit Answers" : "Continue"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-col">
          <Text variant="sm" className="font-medium">
            {currentIndex + 1}. {currentQuestion.label}
          </Text>
          {currentQuestion.allowMultiple && (
            <Text variant="xs" className="text-muted-foreground">
              Select all that apply
            </Text>
          )}
        </div>

        {currentQuestion.allowMultiple ? (
          <div className="space-y-1">
            {currentQuestion.options.map((option: { id: string; label: string }) => (
              <Label
                key={option.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-primary/5"
              >
                <Checkbox
                  checked={(multiSelections[currentQuestion.id] ?? []).includes(option.id)}
                  onCheckedChange={() => {
                    if (!isInteractive) return;
                    toggleMultiSelection(currentQuestion.id, option.id);
                  }}
                  disabled={!isInteractive}
                />
                <Text variant="xs">{option.label}</Text>
              </Label>
            ))}
            {currentQuestion.allowCustom && (
              <Label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-primary/5">
                <Checkbox
                  checked={(multiSelections[currentQuestion.id] ?? []).includes(
                    AgentTools.CUSTOM_OPTION_ID,
                  )}
                  onCheckedChange={() => {
                    if (!isInteractive) return;
                    toggleMultiSelection(currentQuestion.id, AgentTools.CUSTOM_OPTION_ID);
                  }}
                  disabled={!isInteractive}
                />
                <Text variant="xs">Other</Text>
              </Label>
            )}
          </div>
        ) : (
          <RadioGroup
            value={singleSelections[currentQuestion.id] ?? ""}
            onValueChange={(value) => {
              if (!isInteractive) return;
              if (typeof value !== "string") return;
              setSingleSelection(currentQuestion.id, value);
            }}
            className="space-y-1"
          >
            {currentQuestion.options.map((option: { id: string; label: string }) => (
              <Label
                key={option.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-primary/5"
              >
                <RadioGroupItem value={option.id} className="size-4" />
                <Text variant="xs">{option.label}</Text>
              </Label>
            ))}
            {currentQuestion.allowCustom && (
              <Label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 transition-colors hover:bg-primary/5">
                <RadioGroupItem value={AgentTools.CUSTOM_OPTION_ID} className="size-4" />
                <Text variant="xs">Other</Text>
              </Label>
            )}
          </RadioGroup>
        )}

        {showCustomInput && (
          <div className="min-h-[40px] pl-2">
            <Input
              autoFocus
              placeholder="Specify..."
              value={customInputs[currentQuestion.id] ?? ""}
              disabled={!isInteractive}
              onChange={(e) => setCustomInput(currentQuestion.id, e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        )}
      </div>
    </PendingHumanToolCard>
  );
}

/**
 * Completed-state renderer for question responses.
 * Contract: reads submitted tool output and renders a stable Q/A summary.
 */
export function ToolAskQuestionsCompleted({ part }: { part: AskQuestions.Part }) {
  if (part.state === "input-streaming" || part.state === "output-error") {
    return null;
  }

  if (AgentTools.isOutputCanceledState(part)) {
    return (
      <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3">
        <Text variant="xs" className="text-muted-foreground">
          Questions canceled
        </Text>
      </div>
    );
  }

  const output = readOutput(part.output);
  if (!output) {
    return null;
  }

  const questions = readQuestions(part.input);
  const { answers } = output;

  return (
    <div className="mb-4 space-y-2 rounded-lg border border-border bg-muted/30 p-3">
      {questions.map((question) => {
        const answer = answers[question.id];

        return (
          <div key={question.id} className="flex flex-col">
            <Text variant="xs" className="text-muted-foreground">
              {question.label}
            </Text>
            <Text variant="sm">{AgentTools.formatQuestionAnswer(question, answer)}</Text>
          </div>
        );
      })}
    </div>
  );
}

function readQuestions(input: unknown): AgentTools.Question[] {
  if (
    !input ||
    typeof input !== "object" ||
    !Array.isArray((input as { questions?: unknown }).questions)
  ) {
    return [];
  }
  return (input as { questions: unknown[] }).questions.filter(isQuestion);
}

function isQuestion(question: unknown): question is AgentTools.Question {
  return (
    !!question &&
    typeof question === "object" &&
    typeof (question as { id?: unknown }).id === "string" &&
    typeof (question as { label?: unknown }).label === "string" &&
    Array.isArray((question as { options?: unknown }).options)
  );
}

function readOutput(output: unknown): { answers: Record<string, string | string[]> } | null {
  if (!output || typeof output !== "object") return null;
  const answers = (output as { answers?: unknown }).answers;
  if (!answers || typeof answers !== "object" || Array.isArray(answers)) return null;
  return { answers: answers as Record<string, string | string[]> };
}
